import React, { useEffect, useRef } from 'react';
import { Box, Divider, makeStyles, Paper } from '@material-ui/core';
import { capitalize } from 'lodash';
import Maybe from 'components/@common/Maybe';
import StyledSkeleton from 'components/@common/Skeleton';
import { ReportEntityType } from 'components/ReportsModule/types';
import MaybeShowErrorMessageWithLoading from '../../../common/MaybeShowErrorMessageWithLoading';
import PrmTitleRow from '../../common/PrmTitleRow';
import LargeBarChart from '../../common/SplitProgressBarChart/LargeBarChart';
import useCommonStyles from '../../common/styles';
import SkillProgressChart from './SkillProgressChart';
import SkillProgressTable from './SkillProgressTable';
import { PrmCurriculumLessonReportResult } from 'types/schema-generated-types';

type Props = {
  entityType: ReportEntityType;
  curriculumLessonReport: PrmCurriculumLessonReportResult;
  setList?: any[];
  selected: any;
  setSelected: any;
  loading: boolean;
};

const SkillsProgress: React.FC<Props> = ({
  entityType,
  setList,
  selected,
  setSelected,
  curriculumLessonReport,
  loading
}) => {
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const containerRef = useRef<HTMLElement>();
  const toPx = (val: any) => (val ? `${val}px` : undefined);

  useEffect(() => {
    if (!setList?.[0]?.id) return;
    setSelected(setList?.[0]?.id);
    // eslint-disable-next-line
  }, [setList?.[0]?.id]);
  const hasCompletedLessons = !!curriculumLessonReport?.averageLessonCompletedCount;
  return (
    <Paper ref={containerRef} className={commonClasses.container}>
      {curriculumLessonReport ? (
        <>
          <Box display="flex" justifyContent="space-between" margin="24px 0">
            <PrmTitleRow
              title={`${capitalize(entityType)} Skills Progress`}
              setList={setList!}
              handleClick={setSelected}
              selected={selected!}
            />
          </Box>
          <Box className={classes.boxContainer}>
            <Box>
              {loading ? (
                <Box marginTop={-10}>
                  <StyledSkeleton
                    width={toPx(containerRef?.current?.clientWidth! - 50)}
                    height={toPx(380)}
                  />
                </Box>
              ) : (
                <SkillProgressChart
                  total={curriculumLessonReport?.totalLessonCount}
                  assigned={curriculumLessonReport?.averageLessonAssignedCount}
                  completed={curriculumLessonReport?.averageLessonCompletedCount}
                />
              )}
            </Box>
            <Divider orientation="vertical" style={{ height: '174px' }} />
            <Box display="flex" flex={1} justifyContent="center" flexBasis={350}>
              {!loading && (
                <Box display="flex" flex={0.7} maxWidth={500}>
                  <Maybe shouldRender={hasCompletedLessons}>
                    <LargeBarChart
                      productCurriculum={curriculumLessonReport?.productCurriculum}
                      completedPercent={
                        curriculumLessonReport?.overallCompletionStats?.metSkillPercentage
                      }
                      supportedPercent={
                        curriculumLessonReport?.overallCompletionStats
                          ?.metSkillWithSupportPercentage
                      }
                    />
                  </Maybe>
                </Box>
              )}
            </Box>
          </Box>
          <Box>
            {!loading && <SkillProgressTable curriculumLessonReport={curriculumLessonReport} />}
          </Box>
        </>
      ) : (
        <>
          <PrmTitleRow
            title={`${capitalize(entityType)} Skills Progress`}
            setList={setList!}
            handleClick={setSelected}
            selected={selected!}
          />
          {loading ? (
            <StyledSkeleton height="200px" width="100%" />
          ) : (
            <MaybeShowErrorMessageWithLoading shouldRender={false} loading={false} />
          )}
        </>
      )}
    </Paper>
  );
};

const useStyles = makeStyles(() => ({
  boxContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-evenly',
    alignItems: 'center',
    flexFlow: 'wrap'
  }
}));

export default SkillsProgress;
